import {Component} from "react";
import TextLabel from "../../controls/TextLabel/TextLabel.mjs";
import Glob from "../../libs/Glob.mjs";
import appLib from "../../libs/appLib.mjs";
import {Button, ButtonGroup, Input, InputGroup, InputGroupText} from "reactstrap";
import AppRefreshIcon from "../../media/app-images/AppRefreshIcon.mjs";
import AppPerimeterIcon from "../../media/app-images/AppPerimeterIcon.mjs";
import AppSynthDataIcon from "../../media/app-images/AppSynthDataIcon.mjs";
import PropTypes from "prop-types";
import AppEditIcon from "../../media/app-images/AppEditIcon.mjs";
import './PerimetersList.css'

const prefix = "PerimetersList";

/**
 * PerimetersList is a Component that shows existing perimeters.
 * @type {Component}
 * @property {Function} [onClick] If provided, rollover and click are managed
 * @property {JSX.Element|Component|string} [clickIcon] Used with onClick, defaults to Edit Icon
 * @property {number} [pageSize] Defaults to 25 items per page
 * @abstract If there is one, the onClick function is used.
 * Please note that it should return a boolean value: if true the list will be automatically reloaded.
 */
const PerimetersList = class extends Component {
	static propTypes = {
		/**
		 * Defaults to 25 items per page
		 * @type {number}
		 */
		pageSize: PropTypes.number,
		/**
		 * If returned value is true/truish, the perimeters list will be reloaded from server (possibly something changed)
		 * @function
		 * @async
		 * @param {Object} perimeter
		 * @return {Promise<any>}
		 */
		onClick: PropTypes.func,
		/**
		 * Icon on rollover, used in case of onClick function
		 */
		clickIcon: PropTypes.object,
		/** Optional: ID of selected perimeter */
		selected: PropTypes.string,
		/**
		 * Optional: filter function
		 * @function
		 * @param {Array<Object>} perimeters
		 * @return {Array<Object>} Filtered values
		 */
		filter: PropTypes.func,
	}

	constructor(props) {
		super(props);
		this.state = {
			by: Glob.getPageParam('by'),
			page: Glob.getPageParam('page'),
			ll: Glob.getLanguageLong(),
			pageSize: props.pageSize || 25,
			clickIcon: this.props.clickIcon || <AppEditIcon/>,
			selected: this.props.selected || null,
		};
		this.uid = Glob.generateUid(prefix);
		this.fetchPerimeters = this.fetchPerimeters.bind(this);
		this.sortBy = this.sortBy.bind(this);
		this.sort = this.sort.bind(this);
		this.pageNavigation = this.pageNavigation.bind(this);
		this.autoSort = this.autoSort.bind(this);
		this.clickAction = this.clickAction.bind(this);
	}

	componentDidMount() {
		Glob.register('currentPage', this.autoSort, this.uid);
		Glob.register('Prefs', () => {
			const ll = Glob.getLanguageLong();
			if (ll !== this.state.ll) this.setState({ll: ll}, this.fetchPerimeters);
		}, this.uid)
		setTimeout(this.fetchPerimeters);
	}

	componentWillUnmount() {
		Glob.deregister(this.uid);
	}

	async clickAction(peri) {
		if (typeof this.props.onClick === 'function' && peri && typeof peri === "object") {
			const p = structuredClone(peri);
			delete p.searchable;
			const toBeReloaded = await this.props.onClick(p);
			if (toBeReloaded) setTimeout(this.fetchPerimeters);
		}
	}

	pageNavigation() {
		const maxPages = this.state.pages;
		if (!maxPages || maxPages < 2) return null;
		const cp = parseInt(this.state.page);
		const out = [];
		for (let p = 1; p <= maxPages; p++) out.push(p);
		return (
			<div className="pageNavigation">
				{out.map(p => (
					<div
						key={p}
						className={p === cp ? "current" : "active"}
						onClick={() => {
							if (p !== cp) Glob.setCurrentPageParam('page', p);
						}}
					>
						{p}
					</div>
				))}
			</div>
		)
	}

	fetchPerimeters() {
		if (this.state.resortTimeout) try {
			clearTimeout(this.state.resortTimeout);
		} catch (e) {
		}
		this.setState({loading: true, resortTimeout: false}, async () => {
			let perims = (await appLib.api('perimetri/list', {}))?.list || [];
			perims.forEach(p => {
				const s = [p.id];
				if (p.name) {
					s.push(p.name.toLocaleLowerCase(this.state.ll).trim());
				} else {
					p.name = p.id;
				}
				if (p.description) s.push(p.description.toLocaleLowerCase(this.state.ll).trim());
				p.searchable = s.join(' ');
			});
			if (this.props.filter) perims = this.props.filter(perims);
			this.setState({perims: perims}, this.sort);
		});
	}

	autoSort(cp) {
		if (!cp) cp = Glob.getCurrentPage();
		const ns = {};
		if (cp.params.by !== this.state.by) ns.by = cp.params.by;
		if (cp.params.page !== this.state.page) ns.page = cp.params.page;
		if (cp.params.pageSize && parseInt(cp.params.pageSize) !== this.state.pageSize) ns.pageSize = parseInt(cp.params.pageSize);
		if (cp.params.filter !== this.state.filter) ns.filter = cp.params.filter;
		if (this.state.resortTimeout) {
			try { clearTimeout(this.state.resortTimeout); } catch (e) { }
			ns.resortTimeout = false;
		}
		this.setState(ns, this.sort);
	}

	sortBy(axle) {
		if (axle === 'name') axle = undefined;
		if (axle === this.state.by) return;
		if (axle) {
			Glob.setCurrentPageParam('by', axle);
		} else {
			Glob.rmCurrentPageParam('by');
		}
	}

	sort() {
		if (this.state.resortTimeout) try {
			clearTimeout(this.state.resortTimeout);
		} catch (e) {
		}
		const by = this.state.by || 'name';
		const currentPage = Number(this.state.page ?? 1);
		let page = !isNaN(currentPage) ? currentPage : 1;
		let range = structuredClone(this.state.perims || []);
		if (this.state.filter) {
			const f = this.state.filter.toLocaleLowerCase(this.state.ll).trim();
			range = range.filter(peri => peri.searchable.includes(f));
		}
		const pages = Math.ceil(range.length / this.state.pageSize);
		range.sort((a, b) => a.name.toLocaleLowerCase(this.state.ll).localeCompare(b.name.toLocaleLowerCase(this.state.ll)));
		switch (this.state.by) {
			case 'description':
				range.sort((a, b) => (a.description ?? '').toLocaleLowerCase(this.state.ll).localeCompare((b.description ?? '').toLocaleLowerCase(this.state.ll)));
				break;
			case 'creationDate':
				range.sort((a, b) => b.creationDate - a.creationDate);
				break;
			case 'simulations':
				range.sort((a, b) => (b.simulations||[]).length - (a.simulations||[]).length );
				break;
			default:
				break;
		}
		if ((page - 1) * this.state.pageSize > range.length) page = 1;
		range = range.slice((page - 1) * this.state.pageSize, page * this.state.pageSize);
		this.setState(
			{
				range: range,
				by: by,
				page: String(page),
				pages: pages,
				loading: false,
				//resortTimeout: setTimeout(this.sort, 5000)
			},
			() => {
				if (page !== currentPage) Glob.setCurrentPageParam('page', String(page));
			}
		);
	}

	render() {
		/*
		  state:
		  - list: the full list of perimeters from server
		  - range: the window of perimeters to be shown
		 */
		if (!this.state.range) return null;
		return (
			<div className={prefix}>
				<div className={"control-buttons"} hidden={!!this.props.hideControls}>
					<InputGroup>
						<Input
							type="text"
							value={this.state.filter || ''}
							onChange={e => {
								this.setState({filter: e.target.value}, this.sort);
							}}
							placeholder={Glob.label('Filter')}
						/>
						<InputGroupText>
							<ButtonGroup
								className="btn-icon"
							>
								<Button
									disabled={!!this.state.loading}
									onClick={this.fetchPerimeters}
									color={"primary"}
								>
									<b>
										<TextLabel value={"Reload"}/>
									</b>
									<i>
										<AppRefreshIcon/>
									</i>
								</Button>
							</ButtonGroup>
						</InputGroupText>
					</InputGroup>
				</div>
				{(this.state.range.length > 0) ? (
					<div className={"table-container"}>
						{this.pageNavigation()}
						<table
							className={"peri-list" + (typeof this.props.onClick === 'function' ? ' clickable' : '')}
							cellSpacing={0}
							cellPadding={0}
							border={0}
						>
							<thead>
							<tr>
								<th className={"peritype"}><span/></th>
								<th
									role={"button"}
									onClick={() => {
										this.sortBy('name')
									}}
									className={"name " + (this.state.by === 'name' ? 'active' : 'inactive')}
								>
								<span>
									<TextLabel value={"Name"}/>
								</span>
								</th>
								<th
									role={"button"}
									onClick={() => {
										this.sortBy('description')
									}}
									className={"desc nomobile " + (this.state.by === 'description' ? 'active' : 'inactive')}
								>
									<span> <TextLabel value={"Description"}/> </span>
								</th>
								<th
									role={"button"}
									onClick={() => {
										this.sortBy('simulations')
									}}
									title={Glob.label("Simulations")}
									className={"simulations " + (this.state.by === 'simulations' ? 'active' : 'inactive')}
								>
									<span>#</span>
								</th>
								<th
									role={"button"}
									onClick={() => {
										this.sortBy('creationDate')
									}}
									className={"desc nomobile " + (this.state.by === 'creationDate' ? 'active' : 'inactive')}
								>
									<span> <TextLabel value={"CreationDate"}/> </span>
								</th>
							</tr>
							</thead>
							<tbody>
							{this.state.range.map((perim) => (
								<tr
									className={
										(perim.finalized ? 'finalized' : 'pending') +
										(this.state.selected === perim.id ? " selected " : "")
									}
									key={perim.id}
									onClick={() => {
										this.clickAction(perim)
									}}
								>
									<td className={'peritype ' + (perim.fake ? 'fake' : 'real')}>
										{this.state.clickIcon}
										{perim.fake ? <AppSynthDataIcon/> : <AppPerimeterIcon/>}
									</td>
									<td className={"name"}>
										<div>
											{perim.name ?? perim.id}
										</div>
									</td>
									<td className={"desc no-mobile"}>
										<div>
											{perim.description ?? ''}
										</div>
									</td>
									<td className={"simulations"}>
										{ (perim.simulations && perim.simulations.length > 0) ? perim.simulations.length : '-' }
									</td>
									<td className={"creationDate no-mobile"}>
										<div
											title={new Date(perim.creationDate).toLocaleTimeString(this.state.ll)}
										>
											{new Date(perim.creationDate).toLocaleDateString(this.state.ll)}
										</div>
									</td>
								</tr>
							))}
							</tbody>
						</table>
					</div>
				) : (
					<h2 className={"limited"}>
						<TextLabel value={"No items found"}/>
					</h2>
				)}
			</div>
		)
	}
}

export default PerimetersList;
